import { useCallback, useRef, useState } from 'react';
import type { ListWindow } from '@/shared/api';

/** Параметры постраничной загрузки набора для DataGrid в режиме `lazy`. */
export type UseLazyGridPagesParams<TItem> = {
  /**
   * Сигнатура запроса: её смена начинает набор заново.
   *
   * Только строка — сравнение идёт по значению. Составной ключ из нескольких условий потребитель
   * сводит к строке сам: объект, собранный в теле рендера, был бы новым на каждом рендере и
   * перезапускал бы набор без конца.
   */
  cacheVersion: string;
  /** Загрузка одного окна выдачи. */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<ListWindow<TItem>>;
  /** Колбэк первой непустой порции — для той части экрана, что строится по самим данным. */
  onLoaded?: (items: TItem[]) => void;
};

/** Готовые к передаче в `dataSource` величины постраничного набора. */
export type UseLazyGridPagesResult<TItem> = {
  /** Общее число записей выборки — им подписан набор и по нему рассчитана прокрутка */
  totalCount: number;
  /** Ключ пересоздания грида: меняется, только пока набор пуст */
  gridKey: string;
  /** Загрузка одного окна для `dataSource` в режиме `lazy` */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<TItem[]>;
};

/**
 * Постраничная загрузка набора для DataGrid в режиме `lazy`: прокрутка доводит до последней
 * записи выборки, а подпись называет их общее число.
 *
 * При смене сигнатуры запроса общее число сбрасывается: грид высчитывает размер окна как
 * min(pageSize, totalCount - offset), и от устаревшего числа (после отбора с малым числом
 * совпадений) уходит нестандартный размер, не попадающий в общий кэш с обычным запросом.
 *
 * Пока набор пуст — грид пересоздаётся (`gridKey`). Lazy-источник кита запрашивает первое окно
 * только при монтировании, пока общее число неизвестно, а на смену сигнатуры перезапрашивает лишь
 * уже показанный диапазон строк. У пустого набора такого диапазона нет: сброс выбрасывает
 * пришедший ответ, новый запрос не уходит, и набор залипает в загрузке до перезагрузки страницы.
 * У непустого диапазон известен — там грид сохраняется, чтобы не терять позицию прокрутки и
 * ширины колонок.
 */
export const useLazyGridPages = <TItem>({
  cacheVersion,
  fetchPage,
  onLoaded,
}: UseLazyGridPagesParams<TItem>): UseLazyGridPagesResult<TItem> => {
  const [totalCount, setTotalCount] = useState(0);

  const lastCacheVersionRef = useRef(cacheVersion);
  const gridKeyRef = useRef(cacheVersion);
  if (lastCacheVersionRef.current !== cacheVersion) {
    lastCacheVersionRef.current = cacheVersion;
    if (totalCount === 0) gridKeyRef.current = cacheVersion;
    setTotalCount(0);
  }

  const isLoadedRef = useRef(false);

  const loadPage = useCallback(
    async (offset: number, limit: number, signal: AbortSignal) => {
      const page = await fetchPage(offset, limit, signal);
      // Запрос отменён (сменились параметры или грид размонтирован) — его ответ уже неактуален
      if (signal.aborted) return [];
      setTotalCount(page.total);
      if (page.items.length && !isLoadedRef.current) {
        isLoadedRef.current = true;
        onLoaded?.(page.items);
      }
      return page.items;
    },
    [fetchPage, onLoaded],
  );

  return { totalCount, gridKey: gridKeyRef.current, fetchPage: loadPage };
};
